import { Component, OnInit } from '@angular/core'
import { MessageService } from 'primeng/api'

import { CatalogEntry, CopyCandidate, CopyClientDocumentsPreview, CopyFileSelection, DocumentsCatalog, HolderType, SectionMeta } from '@data/interfaces/documents.interface'
import { LeaseDetailService } from '@modules/private/lease/data/services/view/lease-detail.service'
import { ApiDocumentsService } from '@data/services/modules/documents/api-documents.service'
import { ApiLeaseService } from '@modules/private/lease/data/services/api/api-lease.service'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { nombreLegibleDeUrl } from '@data/utils/descarga-archivo.util'

/**
 * A un arrendamiento cerrado ya no se le copian documentos: el API lo rechaza con 409 `LEASE_CLOSED`. No es
 * `FROZEN_STATES`: `Activo` sí copia. Es espejo de `CLOSED_LEASE_STATUSES` en
 * `api-rendafin/src/modules/leases/utils/leaseStatusTransitions.ts`: si allá cambia la lista, se cambia aquí.
 */
const CLOSED_STATUSES: ReadonlyArray<string> = ['Liquidado', 'Cancelado']

export interface CopyFileRow {
  key: string
  candidate: CopyCandidate
  fieldLabel: string
  fileName: string
}

export interface CopyUnitGroup {
  key: string
  title: string
  newInLease: boolean
  sections: { key: string; label: string; rows: CopyFileRow[] }[]
}

/**
 * Documentos del arrendamiento (ADR-0024): la copia de solo lectura del expediente del cliente (`lease.documents`).
 *
 * F-009 · El expediente se sube en el cliente; aquí solo se copian sus archivos aprobados, y quien copia marca cuáles
 * archivo por archivo. Quien tiene `leases.update` ve el aviso «N aprobados nuevos sin copiar» y abre el diálogo con el
 * preview (`dryRun`) del API. Un arrendamiento Liquidado o Cancelado no pide el preview: ya no recibe copias.
 */
@Component({
  selector: 'app-lease-documents',
  templateUrl: './lease-documents.component.html',
  styleUrls: ['./lease-documents.component.scss'],
  providers: [MessageService],
})
export class LeaseDocumentsComponent implements OnInit {
  canCopy = false
  preview: CopyClientDocumentsPreview | null = null
  previewLoading = false
  previewError = false
  groups: CopyUnitGroup[] = []
  dialogVisible = false
  copying = false
  //* Se guarda lo DESMARCADO: todo lo nuevo nace marcado, y si el diálogo se reabre por un archivo que cambió, lo que
  //* el usuario ya había quitado sigue fuera.
  deselected = new Set<string>()

  private catalog: DocumentsCatalog | null = null
  private sections: SectionMeta[] = []

  constructor(
    public leaseDetailService: LeaseDetailService,
    private apiLease: ApiLeaseService,
    private apiDocuments: ApiDocumentsService,
    private auth: AuthService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    const status = this.leaseDetailService.data?.status
    this.canCopy = !!this.leaseDetailService.data && this.auth.userHasAuthorization('leases', 'update') && !CLOSED_STATUSES.includes(status)
    if (!this.canCopy) return
    this.apiDocuments.getCatalog().subscribe((res) => {
      this.catalog = res?.catalog ?? null
      this.sections = res?.sections ?? []
      this.buildGroups()
    })
    this.loadPreview()
  }

  get pendingToCopy(): number {
    return this.preview?.canCopy ? this.preview.pendingToCopy : 0
  }

  get pendingLabel(): string {
    const n = this.pendingToCopy
    return n === 1 ? '1 aprobado nuevo sin copiar' : `${n} aprobados nuevos sin copiar`
  }

  /** «2 por revisar y 1 rechazado no se copian»; vacío si no hay nada fuera. */
  get excludedNote(): string {
    const pending = this.preview?.excluded?.pending ?? 0
    const rejected = this.preview?.excluded?.rejected ?? 0
    const parts: string[] = []
    if (pending > 0) parts.push(`${pending} por revisar`)
    if (rejected > 0) parts.push(`${rejected} ${rejected === 1 ? 'rechazado' : 'rechazados'}`)
    if (!parts.length) return ''
    return `${parts.join(' y ')} ${pending + rejected === 1 ? 'no se copia' : 'no se copian'}: solo se copian archivos aprobados.`
  }

  get selectedFiles(): CopyFileSelection[] {
    return (this.preview?.candidates ?? []).filter((c) => !c.isAlreadyInLease && !this.deselected.has(this.keyOf(c))).map((c) => ({ guarantorId: c.guarantorId, field: c.field, url: c.url }))
  }

  get selectedCount(): number {
    return this.selectedFiles.length
  }

  keyOf(c: { guarantorId?: string | null; field: string; url: string }): string {
    return `${c.guarantorId ?? 'holder'}|${c.field}|${c.url}`
  }

  isSelected(c: CopyCandidate): boolean {
    return !c.isAlreadyInLease && !this.deselected.has(this.keyOf(c))
  }

  toggle(c: CopyCandidate): void {
    if (c.isAlreadyInLease || this.copying) return
    const key = this.keyOf(c)
    if (this.deselected.has(key)) this.deselected.delete(key)
    else this.deselected.add(key)
  }

  openDialog(): void {
    this.deselected = new Set()
    this.dialogVisible = true
  }

  loadPreview(reopen = false): void {
    const leaseId = this.leaseDetailService.data?._id
    if (!leaseId) return
    this.previewLoading = true
    this.previewError = false
    this.apiLease.copyClientDocuments(leaseId, { dryRun: true }).subscribe((r) => {
      this.previewLoading = false
      if (r.error || !r.data) {
        this.previewError = true
        return
      }
      this.preview = r.data
      this.buildGroups()
      //* Con el preview nuevo, lo desmarcado que ya no existe deja de contar.
      const live = new Set(r.data.candidates.map((c) => this.keyOf(c)))
      this.deselected = new Set([...this.deselected].filter((k) => live.has(k)))
      if (reopen) this.dialogVisible = r.data.canCopy && r.data.pendingToCopy > 0
      else if (!r.data.canCopy) this.dialogVisible = false
    })
  }

  copy(): void {
    const leaseId = this.leaseDetailService.data?._id
    const files = this.selectedFiles
    if (!leaseId || this.copying || !files.length) return
    this.copying = true
    this.apiLease.copyClientDocuments(leaseId, { files }).subscribe((r) => {
      this.copying = false
      if (r.error || !r.data) {
        this.handleCopyError(r.msg, r.code, r.status)
        return
      }
      const { added, stale } = r.data
      if (added > 0) this.notifyCopied(added)
      //* Con 0 agregados y algo `stale`, el aviso de abajo ya lo explica: «el arrendamiento ya los tenía» sería falso.
      else if (!stale.length) this.messageService.add({ severity: 'info', summary: 'Sin cambios', detail: 'No se agregó ningún archivo: el arrendamiento ya los tenía.', life: 3500 })
      if (stale.length) {
        this.messageService.add({
          severity: 'warn',
          summary: 'El expediente del cliente cambió',
          detail:
            stale.length === 1
              ? '1 archivo cambió desde que abriste el diálogo y no se copió. Revisa la selección de nuevo.'
              : `${stale.length} archivos cambiaron desde que abriste el diálogo y no se copiaron. Revisa la selección de nuevo.`,
          life: 6000,
        })
        this.loadPreview(true)
        return
      }
      this.dialogVisible = false
      this.loadPreview()
    })
  }

  /** Toast de lo copiado y recarga del expediente; si la recarga falla, lo que se ve está desactualizado y se dice. */
  private notifyCopied(added: number): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Documentos copiados',
      detail: added === 1 ? 'Se copió 1 archivo al arrendamiento.' : `Se copiaron ${added} archivos al arrendamiento.`,
      life: 3500,
    })
    this.leaseDetailService.refreshDocuments().subscribe((isRefreshed) => {
      if (isRefreshed) return
      this.messageService.add({
        severity: 'warn',
        summary: 'Expediente sin actualizar',
        detail: 'Los documentos se copiaron, pero no se pudo recargar el expediente del arrendamiento. Recarga la página para verlo.',
        life: 6000,
      })
    })
  }

  private handleCopyError(msg: string, code?: string, status?: number): void {
    if (code === 'LEASE_CLOSED') {
      this.messageService.add({ severity: 'warn', summary: 'Arrendamiento cerrado', detail: msg || 'Este arrendamiento ya no recibe documentos del cliente.', life: 5000 })
      this.dialogVisible = false
      if (this.preview) this.preview = { ...this.preview, canCopy: false, code: 'LEASE_CLOSED', message: msg }
      return
    }
    //* Los 4xx del API traen un mensaje en español para quien opera (el 400 «Recarga la página…» va tal cual); un 5xx
    //* o una caída de red no: ahí va el genérico.
    const apiMessage = status && status >= 400 && status < 500 && msg ? msg : ''
    this.messageService.add({ severity: 'error', summary: 'No se copiaron los documentos', detail: apiMessage || 'No se pudieron copiar los documentos del cliente. Intenta de nuevo.', life: 5000 })
  }

  /** Titular y cada aval → sección del catálogo → archivo, en el orden del catálogo. */
  private buildGroups(): void {
    const candidates = this.preview?.candidates ?? []
    const units = new Map<string, { title: string; type: HolderType; newInLease: boolean; items: CopyCandidate[] }>()
    for (const c of candidates) {
      const key = c.guarantorId ?? 'holder'
      if (!units.has(key)) {
        const type = c.guarantorId ? (c.guarantorType ?? 'PF') : this.holderType
        const title = c.guarantorId ? `${c.guarantorName?.trim() || 'Aval'} · ${type}` : 'Titular'
        units.set(key, { title, type, newInLease: !!c.guarantorId && c.isNewGuarantorInLease, items: [] })
      }
      units.get(key)!.items.push(c)
    }
    //* El titular primero; los avales en el orden en que llegan.
    const ordered = [...units.entries()].sort(([a], [b]) => (a === 'holder' ? -1 : b === 'holder' ? 1 : 0))
    this.groups = ordered.map(([key, unit]) => ({ key, title: unit.title, newInLease: unit.newInLease, sections: this.sectionsOf(key === 'holder' ? 'holder' : 'guarantor', unit.type, unit.items) }))
  }

  private sectionsOf(role: 'holder' | 'guarantor', type: HolderType, items: CopyCandidate[]): CopyUnitGroup['sections'] {
    const entries: CatalogEntry[] = this.catalog?.[role]?.[type] ?? []
    const entryOf = (field: string) => entries.find((e) => e.field === field)
    const bySection = new Map<string, CopyFileRow[]>()
    const sorted = [...items].sort((a, b) => this.catalogIndex(entries, a.field) - this.catalogIndex(entries, b.field))
    for (const c of sorted) {
      const entry = entryOf(c.field)
      const section = entry?.section ?? 'others'
      const rows = bySection.get(section) ?? []
      rows.push({ key: this.keyOf(c), candidate: c, fieldLabel: entry?.label ?? c.field, fileName: c.name?.trim() || nombreLegibleDeUrl(c.url) || 'Documento' })
      bySection.set(section, rows)
    }
    const order = this.sections.map((s) => s.key as string)
    return [...bySection.entries()]
      .sort(([a], [b]) => this.orderIndex(order, a) - this.orderIndex(order, b))
      .map(([key, rows]) => ({ key, label: this.sections.find((s) => s.key === key)?.label ?? (key === 'others' ? 'Otros' : key), rows }))
  }

  private catalogIndex(entries: CatalogEntry[], field: string): number {
    const i = entries.findIndex((e) => e.field === field)
    return i < 0 ? Number.MAX_SAFE_INTEGER : i
  }

  private orderIndex(order: string[], key: string): number {
    const i = order.indexOf(key)
    return i < 0 ? Number.MAX_SAFE_INTEGER : i
  }

  /** PF o PM del titular: lo dice el tipo de expediente del arrendamiento o, si aún no tiene, el cliente. */
  private get holderType(): HolderType {
    const data = this.leaseDetailService.data
    if (data?.documentType === 'NaturalEntityDocs') return 'PF'
    if (data?.documentType === 'LegalEntityDocs') return 'PM'
    return data?.client?.entity === 'Física' ? 'PF' : 'PM'
  }
}
